import { Config } from 'config';

import { forEach, map, find, last, now } from 'lodash';

import { Base, isArray, cloneDeep, findByType, resolve, if$,
         dot, strokeDot, strokeCross, strokeSegment } from 'gear';

import { Point, Matrix, Bounds, Frame, Segment, Cubic, Curve,
         Color, Process, Envelope } from 'cdl';

import { Tool, padScaling } from '../view';

const Point_add = Point.add,
      Point_substract = Point.substract,
      Point_sm = Point.sm,
      Color_fromRgb = Color.fromRgb,
      knotSize = 3,
      knotSelectedSize = 4;

// [TODO] directly use fillStyle colors here, and flatten this object
const colors = {
  pad                : new Color({rgb:'FFFFFF',alpha:0.7}),
  curve              : Color_fromRgb('8888FF'),
  knot               : Color_fromRgb('4444FF'),
  knot_selected      : Color_fromRgb('000000'),
  knot_over          : Color_fromRgb('44AA88'),
  controlSegment     : Color_fromRgb('4444FF'),
  curveControlSegment: Color_fromRgb('9944CC'),
  cuspControlSegment : Color_fromRgb('AA4433'),
  nextControlSegment : Color_fromRgb('666666'),
  control            : Color_fromRgb('0000FF'),
  control_over       : Color_fromRgb('44AA88'),
  addKnot            : Color_fromRgb('000000'),
  addKnot_over       : Color_fromRgb('44AA88'),
  removeKnot         : Color_fromRgb('FF0000'),
  removeKnot_over    : Color_fromRgb('44AA88'),
  dragSelector       : Color_fromRgb('009900'),
  curvePadBack       : Color_fromRgb('E8E8FF')
};

function drawDot( context, style, size, point, transform, textureScale ) {
  context.fillStyle = colors.pad.rgba;
  dot(context,point,(size+1.4)*textureScale,transform);
  context.fillStyle = style;
  dot(context,point,size*textureScale,transform);
}

function drawSegment( context, style, p0, p1, transform, textureScale ) {
  context.lineWidth = 3.5 * textureScale;
  context.strokeStyle = colors.pad.rgba;
  strokeSegment(context,p0,p1,transform);
  context.lineWidth = 1.5 * textureScale;
  context.strokeStyle = style;
  strokeSegment(context,p0,p1,transform);
}

// States
//   Iddle: nothing is selected, waiting for user input
//   DragSelection: user is selecting multiple knots with a rectangle
//   EditingKnot: one knot is selected, user can move it and move the control points
//   SelectedKnots:
//   MovingControlPoint: control point is being moved
//   MovingKnots: multiple knots are selected

function nextKi( side, curve, ki ) {
  const length = curve.pieces.length, closed = curve.closed;
  if( side === 'left' ) {
    if( !closed && ki === 0 ) {
      return null;
    }
    return ( ki === 0 && closed ) ? length-1 : ki-1;
  }
  else { // right

    if( !closed && ki === length ) {
      return null;
    }
    return (ki === length-1 && closed ) ? 0 : ki+1;
  }
}

function knotPoint( curve, ki ) {
  const pieces = curve.pieces;
  if( ki === null ) {
    return null;
  }
  if( ki >= pieces.length ) {
    return last(pieces).last();
  }
  return pieces[ki].P0;
}

function nextKnotPoint( side, curve, ki ) {
  return knotPoint(curve,nextKi(side,curve,ki));
}

function pieceIndexAt( side, curve, ki ) {
  if( side === 'left' ) {
    return nextKi(side,curve,ki);
  }
  else {
    return ! curve.closed && ki === curve.pieces.length ? null : ki;
  }
}

function pieceAt( side, curve, ki ) {
  const pi = pieceIndexAt(side,curve,ki);
  return pi !== null ? curve.pieces[pi] : null;
}

function controlPoint( side, curve, ki ) {
  const { pieces } = curve;
  let piece;
  if( ki === null ) {
    return null;
  }
  if( side === 'left' ) {
    piece = null;
    if( ki === 0 && curve.closed) {
      piece = last(pieces);
    }
    else {
      piece = pieces[ki-1];
    }
    if( piece && piece.type === 'Cubic' ) {
      return piece.P2;
    }
    return null;
  }
  else { // 'right'
    if( ki >= pieces.length ) {
      return null;
    }
    piece = pieces[ki];
    if( piece && piece.type === 'Cubic' ) {
      return piece.P1;
    }
    return null;
  }
}

function nextControlPoint( side, curve, ki ) {
  return controlPoint(oppSide(side),curve,nextKi(side,curve,ki));
}

function removeKnot( curve, ki, tool ) {

  // Check if it is the last knot
  if( curve.pieces.length === 1 ) {
    // [TODO] Curves should be included in the type system, remove lazy loading, is not gaining us much
    const region = curve._owner;
    const curves = region._curves;
    curves.splice( curves.indexOf(curve), 1 );

    if( curves.length === 0 ) {
      const polyregion = region._owner;
      const regions = polyregion._regions;

      regions._remove(region);
      if( regions.length === 0 ) {
        const figure = polyregion._owner;
        figure._polyregions._remove( polyregion );
      }
    }
    const toolCurves = tool._curves;
    toolCurves.splice( toolCurves.indexOf(curve), 1 );
    return;
  }

  const pieces = curve.pieces;
  if( ki === pieces.length ) { // open curve
    pieces.pop();
    return;
  }

  const kiLeft = nextKi('left' ,curve,ki);
  if( kiLeft === null ) { // open curve
    pieces.shift();
    return;
  }

  const pieceLeft = pieces[kiLeft];
  const piece     = pieces[ki];

  pieceLeft.last(piece.last());
  if( pieceLeft.P3 ) {
    if( piece.P3 ) { // Both are cubic, try to avoid big modifications in the curve
      pieceLeft.P1 = Point_sm(pieceLeft.P1,2).substract(pieceLeft.P0);
      pieceLeft.P2 = Point_sm(piece.P2    ,2).substract(piece.P3);
    }
    else {
      pieces[kiLeft] = new Segment(pieceLeft.P0,piece.last());
    }
  }
  pieces.splice(ki,1);
  curve.tieExtremePoints();
}

function oppSide( side ) {
  return side === 'left' ? 'right' : 'left';
}

const Knot = function( curve, ki ) {
  this.curve = curve;
  this.ki = ki;
};
Knot.prototype = {
  isEqual( ok ) {
    return ok && this.curve === ok.curve && this.ki === ok.ki;
  },
  point() {
    return knotPoint(this.curve,this.ki);
  },
  nextKi( side ) {
    return nextKi(side,this.curve,this.ki);
  },
  nextPoint( side ) {
    return nextKnotPoint(side,this.curve,this.ki);
  },
  pieceAt( side ) {
    return pieceAt(side,this.curve,this.ki);
  },
  pieceIndexAt( side ) {
    return pieceIndexAt(side,this.curve,this.ki);
  },
  pointAt( side,t) {
    return this.pieceAt(side).eval(t);
  },
  controlPoint( side ) {
    return controlPoint(side,this.curve,this.ki);
  },
  nextControlPoint( side ) {
    return nextControlPoint(side,this.curve,this.ki);
  },
  nextKnot( side ) {
    const nki = nextKi(side,this.curve,this.ki);
    if( nki === null ) {
      return null;
    }
    return new Knot(this.curve,nki);
  },
  tangentAt( side ) {
    const to = this.controlPoint(side) || this.nextPoint(side);
    if( ! to ) {
      return null;
    }
    const from = this.point();
    const tangent = Point_substract(from,to);
    if( tangent.isZero() ) {
      return null;
    }
    return tangent.normalized();
  },
  normal() {
    let leftTangent  = this.tangentAt("left");
    let rightTangent = this.tangentAt("right");
    if( ! leftTangent && ! rightTangent ) {
      leftTangent  = new Point( 1.0,0.0);
      rightTangent = new Point(-1.0,0.0);
    }
    else if( !  leftTangent ) {
      leftTangent = Point.minus(rightTangent);
    }
    else if( ! rightTangent ) {
      rightTangent = Point.minus( leftTangent);
    }

    const normal = new Point( leftTangent.y - rightTangent.y, -leftTangent.x + rightTangent.x);

    return normal.isZero() ? leftTangent : normal.normalize();
  },
  locked() {
    return this.point().__cadx_locked === true;
  },
  // You can not use the knot after calling this function
  remove( tool) {
    removeKnot(this.curve,this.ki,tool);
  },
  addKnot( side ) {
    const curve = this.curve;
    const pi = this.pieceIndexAt(side);
    // [TODO] Move locked annotations out of points
    const locked = curve.pieces[pi].P0.__cadx_locked;
    this.curve.splitPiece(pi,0.5);
    if( locked ) {
      curve.pieces[pi].P0.__cadx_locked = true;
    }
    return new Knot(this.curve, side==='left' ? this.ki : this.ki+1 );
  },
  _computeCorner() {
    const left  = this.controlPoint('left');
    const right = this.controlPoint('right');
    if( left && right ) {
      const point = this.point();
      if( point.isNear(left,0.01) || point.isNear(right,0.01) ) {
        return 'cusp';
      }
      const lp = Point_substract(point,left);
      const rp = Point_substract(right,point);
      if( lp.isNear(rp,0.01) ) {
        return 'symmetric';
      }
      if( Math.abs( (lp.x*rp.y) - (lp.y*rp.x) ) < 0.01 ) {
        return 'curve';
      }
      return 'cusp';
    }
    return 'cusp';
  },
  corner() {
    if( !this._corner ) {
      this._corner = this._computeCorner();
    }
    return this._corner;
  },
  changeCorner( side ) {
    const _side  = this.controlPoint(side);
    const _opp   = this.controlPoint(oppSide(side));
    const point  = this.point();
    const corner = this.corner();
    const newCorner = corner === 'cusp'  ? 'curve'
                  : corner === 'curve' ? 'symmetric'
                                       : 'cusp';
    if( ! ( _side && _opp ) ) {
      return;
    }
    let sh = Point_substract(point,_side);
    if( newCorner === 'symmetric' ) {
      _opp.set( Point_add(point,sh) );
    }
    else if( newCorner === 'curve' ) {
      if( corner === 'cusp' ) {
        const oh = Point_substract(_opp,point);
        sh = Point_sm(sh.normalized(),oh.norm());
        _opp.set( Point_add(point,sh) );
      }
      else { // Symmetric
        sh = Point_add(sh,Point_sm(sh.normalized(),0.1));
        _opp.set( Point_add(point,sh) );
      }
    }
    else if( newCorner === 'cusp' ) {
      const p = sh.perpendicular(-1);
      sh = Point_add(Point_substract(_opp,point),Point_sm(p.normalized(),0.1));
      _opp.set( Point_add(point,sh) );
    }
    this._corner = newCorner;
  },
  convertToCurve( side ) {
    const pieces = this.curve.pieces;
    const kp = side === 'left' ? nextKi(side,this.curve,this.ki) : this.ki;
    const p = pieces[kp];
    pieces.splice(kp,1,Cubic.fromSegment(p.P0,p.P1));
    this.curve.tieExtremePoints();
    this._corner = null;
  },
  convertToSegment( side ) {
    const pieces = this.curve.pieces;
    const kp = side === 'left' ? nextKi(side,this.curve,this.ki) : this.ki;
    const p = pieces[kp];
    pieces.splice(kp,1,new Segment(p.P0,p.last()));
    this.curve.tieExtremePoints();
    this._corner = null;
  }
};

function forEachKnot( curve, func ) {
  const pieces = curve.pieces;
  for(let n = 0, nEnd = pieces.length; n < nEnd; ++n ) {
    if( func(new Knot(curve,n)) === false ) {
      return curve;
    }
  }
  if( ! curve.closed ) {
    if( func(new Knot(curve,curve.pieces.length)) === false ) {
      return curve;
    }
  }
  return curve;
}

function findKnot( curve, func ) {
  const pieces = curve.pieces;
  let k;
  for(let n = 0, nEnd = pieces.length; n < nEnd; ++n ) {
    k = new Knot(curve,n);
    if( func(k) === true ) {
      return k;
    }
  }
  if( ! curve.closed ) {
    k = new Knot(curve,pieces.length);
    if( func(k) === true ) {
      return k;
    }
  }
  return null;
}

const BaseState = Base.extend({
  init( tool ) {
    this._tool = tool;
    this._padSize = 0.1 * 96;
    this._cursor = null;
    this.showToolhelp();
  },
  showToolhelp() {
    this._tool.toolhelp(this.toolhelp().concat(this.baseToolhelp()));
  },
  toolhelp() {
    return [];
  },
  baseToolhelp() {
    return ['Click or Draw rectangle to select knots',
            'Enter to Commit, Escape to Cancel'];
  },
  undoState() {
    this._tool.undoState();
  },
  state( s, k, a, b, c ) {
    this._tool.state(s,k,a,b,c);
  },

  curveToDoc( point ) {
    return this.matrix.apply(point);
  },

  viewToCurve_dx( d ) {
    return this.matrixInverted.scaleFactor() * this.view.viewDeltaToDoc(d);
  },

  curvePoint({ docPoint }) {
    return this.matrixInverted.apply( docPoint );
  },

  get textureScale() {
    return this.view.viewDeltaToTexture(1);
  },

  get view() {
    return this._tool.view;
  },
  get matrix() {
    return this._tool._matrix;
  },
  get matrixInverted() {
    return this._tool._matrixInverted;
  },

  focus() {
    this.showToolhelp();
    this._possibleClick = null;
  },

  get cursor() {
    return this._tool.cursor;
  },
  set cursor( cursor_ ) {
    this._tool.cursor = cursor_;
  },

  redraw() {
    this._tool.redraw();
  },
  onUpdate() {
    this._tool.onUpdate();
  },

  draw( context, transform ) {
    this.drawCurve(context,transform);
    this.drawKnots(context,transform);
  },

  drawCurve( context, transform ) {
    const { textureScale } = this;
    context.lineWidth = 3.5 * textureScale;
    context.strokeStyle = colors.pad.rgba;
    this._strokeCurve(context,transform);
    context.lineWidth = 1.5 * textureScale;
    context.strokeStyle = colors.curve.rgba;
    this._strokeCurve(context,transform);
  },

  _strokeCurve( context, transform ) {
    forEach( this._tool._curves, curve => {
      context.beginPath();
      curve.draw(context,transform);
      context.stroke();
    });
  },

  drawKnots( context, transform ) {
    const tool = this._tool;
    const over_knot = this._over_knot;
    const { textureScale } = this;
    forEach( tool._curves, curve => {
      forEachKnot( curve, knot => {
        const ok = knot.isEqual(over_knot);
        drawDot(context, ok ? colors.knot_over.rgba : colors.knot.rgba, ok ? knotSelectedSize : knotSize,
                knot.point(),transform,textureScale);
      });
    });
    const fillStyle = this._over_selectedKnots ? colors.knot_over.rgba : colors.knot_selected.rgba;
    forEach( this.selectedKnots(), knot => {
      drawDot(context,fillStyle,knotSelectedSize,
              knot.point(),transform,textureScale);
    });
  },

  overKnot( event ) {
    const padSize = this.viewToCurve_dx(this._padSize*padScaling),
          point = this.curvePoint(event);
    let overKnot = null;
    find( this._tool._curves, curve => {
      overKnot = findKnot( curve, knot => point.isNear(knot.point(),padSize) );
      if( overKnot ) {
        return true;
      }
    });
    return overKnot;
  },

  overSelectedKnots( event ) {
    const padSize = this.viewToCurve_dx(this._padSize*padScaling),
          point = this.curvePoint(event);

    return find( this.selectedKnots(), knot => point.isNear(knot.point(),padSize) );
  },

  selectedKnot() {
    return this._tool._selectedKnots[0];
  },

  selectedKnots() {
    return this._tool._selectedKnots;
  },

  down({ event, docPoint, shiftKey }) {
    const selectedKnots = this.selectedKnots();
    const knot = this.overKnot(event);
    if( knot ) {
      if( shiftKey && selectedKnots ) {
        return this.state('SelectedKnots',selectedKnots.concat(knot));
      }
      else {
        this._possibleClick = { docPoint, knot };
        return true;
      }
    }
    this.state('DragSelection',selectedKnots,docPoint);
    return false;
  },

  move({ event, docPoint }) {
    if( this._possibleClick && ! docPoint.isEqual( this._possibleClick.docPoint ) ) {
      this.state('EditingKnot',this._possibleClick.knot);
      this.state('MovingKnots',this._possibleClick.knot,this.curvePoint(event));
      this._possibleClick = null;
      this.redraw();
    }
  },

  mousemove( event ) {
    this._over_knot = this.overKnot(event);
    this._over_selectedKnots = this.overSelectedKnots(event);
    if( this._changeCursorTo ) {
      this.cursor = this._changeCursorTo;
      this._changeCursorTo = null;
    }
    else if( this._over_knot || this._over_selectedKnots ) {
      this.cursor = 'pointer';
    }
    else{
      this.cursor = 'default';
    }
    this.redraw(); // [TODO] Avoid redrawing when it is not needed
  },

  up( /*view,event*/) {
    if( this._possibleClick ) {
      this.state('EditingKnot',this._possibleClick.knot);
      this._possibleClick = null;
    }
  },

  on_dblclick( /*view,event*/) {
    // ...
  },

  deleteAction() {
    // nothing...
  }
});

const HandleBase = Base.extend({
  init( state, side ) {
    this._state = state;
    this._knot = state._knot;
    this._side = side;
    this._over = false;
    this._point = this.point();
    this._padSize = 0.1 * 96;
  },

  over( event ) {
    const state = this._state;
    const p = this._point;
    return ( p && state.curvePoint(event).isNear( p, state.viewToCurve_dx(this._padSize*padScaling) ) );
  },

  down( event ) {
    if( ! this._point ) {
      return;
    }

    if( this.over(event) ) {
      this.click(event);
      return true;
    }
    return false;
  },

  move( /* event */ ) {

  },

  mousemove( event ) {
    if( ! this._point ) {
      return;
    }
    this._over = this.over(event);
    if( this._over ) {
      this._state._changeCursorTo = 'pointer';
    }
  },

  on_dblclick( event ) {
    if( ! this._point ) {
      return;
    }
    if( this.over(event) ) {
      this.dblClick(event);
      return true;
    }
    return false;
  },

  click( /*view,event*/) {},
  dblClick( /*view,event*/) {},

  drawCurvePadBack( context, transform ) {
    const { textureScale } = this;
    context.strokeStyle = colors.curve.rgba;
    context.lineWidth = 1;
    strokeDot(context,this._point,5*textureScale,transform);
    context.fillStyle = colors.curvePadBack.rgba;
    dot(context,this._point,4.5*textureScale,transform);
  },

  state( a,b,c,d,e) {
    this._state.state(a,b,c,d,e);
  },

  curvePoint( event ) {
    return this._state.curvePoint(event);
  },

  viewToCurve_dx( d ) {
    return this._state.viewToCurve_dx(d);
  },

  onUpdate() {
    this._state.onUpdate();
  },

  get textureScale() {
    return this._state.textureScale;
  }
});


const Handle = {

  'Control': HandleBase.extend({
//==========================================================================

    point() {
      return this._knot.controlPoint(this._side);
    },

    draw( context, transform ) {
      if( ! this._point ) {
        return;
      }

      // [TODO]

      const corner = this._knot.corner();
      const strokeStyle = this._side === 'right' ? colors.controlSegment.rgba
                          : corner === 'cusp'  ? colors.cuspControlSegment.rgba
                          : corner === 'curve' ? colors.curveControlSegment.rgba
                                               : colors.controlSegment.rgba;

      const { textureScale } = this;

      drawSegment(context,strokeStyle,this._knot.point(),this._point,transform,textureScale);

      drawDot(context, this._over ? colors.control_over.rgba : colors.control.rgba, 2,
              this._point,transform,textureScale);
    },

    click( event ) {
      return this.state('MovingControl',this._knot,this._side,this.curvePoint(event));
    },

    dblClick( /*view,event*/) {
      this._knot.changeCorner(this._side);
      this.onUpdate();
      this._state.focus();
    }
  }),

  'NextControl': HandleBase.extend({
//==========================================================================
    point() {
      return this._knot.nextControlPoint(this._side);
    },
    draw( context, transform ) {

      if( ! this._point ) {
        return;
      }

      const { textureScale } = this;

      drawSegment(context,colors.nextControlSegment.rgba,
                  this._knot.nextKnot(this._side).point(),this._point,transform,textureScale);

      drawDot(context,this._over ? colors.control_over.rgba : colors.control.rgba, 2,
              this._point,transform,textureScale);
    },
    click( event ) {
      const nextKnot = this._knot.nextKnot(this._side);
      this.state('EditingKnot',nextKnot);
      this.state('MovingControl',nextKnot,oppSide(this._side),this.curvePoint(event));
    }
  }),

  'AddKnot': HandleBase.extend({
//==========================================================================
    point() {
      // Use the middle of the curve
      const p = this._knot.pieceAt(this._side);
      return p ? p.eval(0.5) : null;
    },

    draw( context, transform ) {
      if( ! this._point ) {
        return;
      }
      this.drawCurvePadBack(context,transform);
      const { textureScale } = this;
      if( this._over ) {
        context.fillStyle = colors.addKnot_over.rgba;
        dot(context,this._point,textureScale*knotSelectedSize,transform);
      }
      else {
        context.strokeStyle = colors.addKnot.rgba;
        context.lineWidth = textureScale*0.5;
        strokeDot(context,this._point,textureScale*2,transform);
      }
    },

    click( event ) {
      const newKnot = this._knot.addKnot(this._side);
      this.state('EditingKnot',newKnot);
      this.state('MovingKnots',newKnot,this.curvePoint(event));
    }
  }),

  'RemoveKnot': HandleBase.extend({
//==========================================================================

    point() {
      const k = this._knot;
      return Point_add(k.point(),Point_sm(k.normal(),this.viewToCurve_dx(30)));
    },

    draw( context, transform ) {
      if( ! this.point ) {
        return;
      }
      const { textureScale } = this;
      // [TODO]
      const lineWidth = ( this._over ? 2 : 1 ) * textureScale;
      const rs = ( this._over ? 4 : 2 ) * textureScale;
      const point = this._point;

      context.lineWidth = lineWidth + 2 * textureScale;
      context.strokeStyle = colors.pad.rgba;
      strokeCross(context,point, rs + 1 * textureScale, transform);

      context.lineWidth = lineWidth;
      context.strokeStyle = colors.removeKnot.rgba;
      strokeCross(context,point,rs,transform);
      if( this._over ) {
        context.lineWidth = lineWidth + 2 * textureScale;
        context.strokeStyle = colors.pad.rgba;
        strokeDot(context,this._knot.point(), (knotSelectedSize + 3) * textureScale,transform);

        context.strokeStyle = colors.removeKnot.rgba;
        context.lineWidth = lineWidth;
        strokeDot(context,this._knot.point(), (knotSelectedSize + 3) * textureScale,transform);
      }
    },
    click( /*view,event*/) {
      this._knot.remove(this._state._tool);
      this.state('Iddle');
      this.onUpdate();
    }
  }),

  'ConvertToCurve': HandleBase.extend({
//==========================================================================
    point() {
      const p = this._knot.pieceAt(this._side);
      if( ! ( p instanceof Segment ) ) {
        return null;
      }
      return p.eval( this._side === 'left' ? 2/3.0 : 1/3.0 );
    },

    draw( context, transform ) {
      if( ! this._point ) {
        return;
      }
      this.drawCurvePadBack(context,transform);

      const { textureScale } = this;

      drawSegment(context,colors.controlSegment.rgba,
                  this._knot.point(),this._point,transform,textureScale);

      context.fillStyle = colors.control.rgba;
      dot(context,this._point, 2 * textureScale ,transform);
    },

    click( event ) {
      this._knot.convertToCurve(this._side);
      this.state('MovingControl',this._knot,this._side,this.curvePoint(event));
      this.onUpdate();
    }
  }),

  'ConvertToSegment': HandleBase.extend({
//==========================================================================
    point() {
      const p = this._knot.pieceAt(this._side);
      if( !p || p instanceof Segment ) {
        return null;
      }
      return p.eval( this._side === 'left' ? 2/3.0 : 1/3.0 );
    },

    draw( context, transform ) {
      if( ! this._point ) {
        return;
      }

      const { textureScale } = this;

      if( this._over ) {
        const piece = this._knot.pieceAt(this._side);
        context.lineWidth = 4 * textureScale;
        context.strokeStyle = colors.pad.rgba;
        context.beginPath();
        piece.draw(context,transform);
        context.stroke();

        context.lineWidth = 2 * textureScale;
        context.strokeStyle = colors.removeKnot.rgba;
        context.beginPath();
        piece.draw(context,transform);
        context.stroke();

        context.lineWidth = 1 * textureScale;
        drawSegment(context,colors.curve.rgba,
                    this._knot.point(),this._knot.nextKnot(this._side).point(),transform,textureScale);
      }

      this.drawCurvePadBack(context,transform);

      context.strokeStyle = colors.removeKnot.rgba;
      context.lineWidth = 1 * textureScale;
      const rs = 2 * textureScale;
      strokeCross(context,this._point,rs,transform);
    },

    click( /*view,event*/) {
      this._knot.convertToSegment(this._side);
      this._state.focus();
    }
  })
};

const State = {

  'Iddle': BaseState.extend({
// =========================================================
  }),

  'EditingKnot': BaseState.extend({
// =========================================================
    init( tool) {
      this.callParent(tool);
      this.focus();
    },
    toolhelp() {
      return [
        'Drag knots and control handles to modify curve',
        'Double Click control handle to change curve type'
      ];
    },
    focus() {
      this.showToolhelp();

      const knot = this._knot = this.selectedKnot();

      this._handles = [];

      const moreHandles = [
        new Handle.AddKnot(this,'left'),
        new Handle.AddKnot(this,'right'),
        new Handle.ConvertToCurve(this,'left'),
        new Handle.ConvertToCurve(this,'right'),
        new Handle.ConvertToSegment(this,'left'),
        new Handle.ConvertToSegment(this,'right')
      ];

      // Only add handles if the buttons aren't that close to the knot
      const knotPoint = knot.point();
      const delta = this.view.viewDeltaToDoc(15);
      forEach( moreHandles, h => {
        const point = h.point();
        if( point && ! point.isNear( knotPoint, delta ) ) {
          this._handles.push(h);
        }
      });

      // Order is important, first move control points, then knots...
      this._handles = this._handles.concat([
        new Handle.Control(this,'left'),
        new Handle.Control(this,'right'),
        new Handle.NextControl(this,'left'),
        new Handle.NextControl(this,'right'),
      ]);

      // Do not let the user remove locked knots
      if( ! this._knot.locked() ) {
        this._handles.push( new Handle.RemoveKnot(this) );
      }
    },

    draw( context, transform ) {
      this.drawCurve(context,transform);
      forEach( this._handles, h => {
        h.draw(context,transform);
      });
      this.drawKnots(context,transform);
    },

    down( event ) {
      if( find( this._handles, h => h.down(event) ) ) {
        return true;
      }
      return this.callParent(event);
    },

    mousemove( event ) {
      forEach( this._handles, h => { h.mousemove(event); });
      this.redraw();
      this.callParent(event);
    },

    move( event ) {
      forEach( this._handles, h => { h.move(event); });
      this.redraw();
      this.callParent(event);
    },

    on_dblclick( event ) {
      find( this._handles, h => h.on_dblclick(event) );
    },

    deleteAction() {
      const knot = this._knot;
      if( ! knot.locked() ) {
        knot.remove(this._tool);
        this.state('Iddle');
        this.onUpdate();
      }
    }

  }),

  'DragSelection': BaseState.extend({
// =========================================================
    init( tool,fromPoint) {
      this.callParent(tool);
      this._fromPoint = fromPoint;
      this._toPoint   = fromPoint;
    },

    draw( context, transform ) {
      this.callParent(context,transform);
      context.strokeStyle = colors.dragSelector.rgba;
      const fixedTransform = Matrix.m( transform, this.matrixInverted );

      const frame = Frame.ofPoints([ this._fromPoint, this._toPoint ]);
      frame.draw(context,fixedTransform);
      context.lineWidth = this.textureScale * 1;
      context.stroke();
    },

    down() {
      this.state('Iddle');
      return false;
    },

    move({ event, docPoint }) {
      this._toPoint = docPoint;
      this.redraw();
    },

    up({ event, shiftKey }) {
      const tool = this._tool;
      let knots = [];
      const db = Bounds.ofPoints([this._fromPoint,this._toPoint]);
      forEach( tool._curves, curve => {
        forEachKnot( curve, knot => {
          if( db.contains( this.curveToDoc(knot.point()) ) ) {
            knots.push(knot);
          }
        });
      });
      if( shiftKey ) {
        knots = this.selectedKnots().concat(knots);
      }
      if( knots.length === 1 ) {
        this.state('EditingKnot',knots[0]);
      }
      else if( knots.length > 0 ) {
        this.state('SelectedKnots',knots);
      }
      else {
        this.state('Iddle');
      }
    }
  }),

  'SelectedKnots': BaseState.extend({
// =========================================================
    init( tool) {
      this.callParent(tool);
    },
    toolhelp() {
      return ['Drag knots to modify curve'];
    },

    focus() {
      this.showToolhelp();
      const c = this._possibleClick;
      if( c && now()-c.time<500 ) {
        this.state('EditingKnot',c.knot);
      }
      else {
        this._possibleClick = null;
      }
    },

    down({ event, shiftKey }) {
      const knot = this.overSelectedKnots(event);
      if( knot ) {
        if( ! shiftKey ) {
          this._possibleClick = { knot: knot, time: now() };
        }
        this.state('MovingKnots',this.selectedKnots(),this.curvePoint(event));
        return true;
      }
      return this.callParent(event);
    },

    move( event ) {
      this.callParent(event);
    },

    deleteAction() {
      const tool = this._tool;
      const knots = this.selectedKnots();
      for(let k = 0, kEnd = knots.length; k < kEnd; k++ ) {
        const knot = knots[k];
        if( ! knot.locked() ) {

          const ki = knot.ki;
          knot.remove(tool);

          // Correct indexes, to be able to continue removing other knots
          // This ended up quite complex because of using a direct view of
          // the curve representation [curve,ki]
          for(let i = k+1, iEnd = knots.length; i < iEnd; i++ ) {
            const knot_i = knots[i];
            if( knot_i.ki > ki ) {
              knot_i.ki--;
            }
          }
        }
      }
      this.state('Iddle');
      this.onUpdate();
    }
  }),

  'MovingKnots': BaseState.extend({
// =========================================================
    init( tool, fromPoint ) {
      this.callParent(tool);
      this._fromPoint = fromPoint;
      this._points = map( this.selectedKnots(), knot => {
        const point = knot.point();
        const left  = knot.controlPoint('left');
        const right = knot.controlPoint('right');
        const r = { point: point, original: point.clone() };
        if( left ) {
          r.left = left;
          r.originalLeft = left.clone();
        }
        if( right ) {
          r.right = right;
          r.originalRight = right.clone();
        }
        return r;
      });

      //filter duplicated selected points
      let lFilteredPoints = [];
      this._points.forEach(p1 => {
        if (!find( lFilteredPoints, p2 => p1.point == p2.point ))
          lFilteredPoints.push(p1);
      });
      this._points = lFilteredPoints;
    },

    down() {
      this.state('Iddle');
      return false;
    },

    cacheOppositeKnot ( aKnotId )
    {
      const lOppositeKnot = new Knot(this._tool._curves[0],aKnotId);
      if (this._oppositeKnotId != aKnotId)
      {
        if (this._oppositeKnotId != undefined)
        {
          this._oppositeKnot.point.set(this._oppositeKnot.original);
          this._oppositeKnot.left.set( this._oppositeKnot.originalLeft);
          this._oppositeKnot.right.set( this._oppositeKnot.originalRight);
        }

        this._oppositeKnotId = aKnotId;
        let lOppositePoint = lOppositeKnot.point();
        let lOppositeLeft  = lOppositeKnot.controlPoint('left');
        let lOppositeRight = lOppositeKnot.controlPoint('right');
        this._oppositeKnot = { point: lOppositePoint, original: lOppositePoint.clone() };
        if( lOppositeLeft ) {
          this._oppositeKnot.left = lOppositeLeft;
          this._oppositeKnot.originalLeft = lOppositeLeft.clone();
        }
        if( lOppositeRight ) {
          this._oppositeKnot.right = lOppositeRight;
          this._oppositeKnot.originalRight = lOppositeRight.clone();
        }
      }
      return this._oppositeKnot;
    },
    move( event ) {
      const fromPoint = this._fromPoint;
      const toPoint   = this.curvePoint(event);
      let diff = Point_substract(toPoint,fromPoint);
      let diffOpposite;
      //This special shortcut works only if the figure is an envelope with no removed or added knots
      //So just 1 curve, with 8 knots
      //Also the user need to have only 1 point selected
      if (    (event.ctrlKey || event.shiftKey) 
          &&  this._points.length == 1
          &&  this._tool._curves.length == 1
          &&  this._tool._curves[0].pieces.length == 8)
      {
        let lIdx = this.selectedKnots()[0].ki;
        const p = this._points[0];
        let lOppositeKnot;
        //lIdx is the envelope knot index:
        // 6 5 4
        // 7   3
        // 0 1 2

        let lOpposite = 0;
        if (  lIdx == 3 //3 and 7 move only right-left
           || lIdx == 7 
           || (Math.abs(diff.x)>Math.abs(diff.y) && lIdx != 1 && lIdx != 5))//1 and 5 cant be moved right-left
        {
          switch (lIdx)
          {
            case 0: lOpposite = 2; break;
            case 2: lOpposite = 0; break;
            case 3: lOpposite = 7; break;
            case 4: lOpposite = 6; break;
            case 6: lOpposite = 4; break;
            case 7: lOpposite = 3; break;
          }
          diff = new Point(diff.x,0);
          lOppositeKnot = this.cacheOppositeKnot(lOpposite); 
          if (event.shiftKey)
            diffOpposite = new Point(-diff.x,p.original.y-lOppositeKnot.original.y);
          else
            diffOpposite = new Point( diff.x,p.original.y-lOppositeKnot.original.y);
        }
        else
        {
          switch (lIdx)
          {
            case 0: lOpposite = 6; break;
            case 1: lOpposite = 5; break;
            case 2: lOpposite = 4; break;
            case 4: lOpposite = 2; break;
            case 5: lOpposite = 1; break;
            case 6: lOpposite = 0; break;
          }
          diff = new Point(0,diff.y);
          lOppositeKnot = this.cacheOppositeKnot(lOpposite); 
          if (event.shiftKey)
            diffOpposite = new Point(p.original.x-lOppositeKnot.original.x, -diff.y);
          else
            diffOpposite = new Point(p.original.x-lOppositeKnot.original.x,  diff.y);
        }
        p.point.set( Point_add(p.original,diff) );
        p.left.set( Point_add(p.originalLeft ,diff) );
        p.right.set( Point_add(p.originalRight,diff) );
        lOppositeKnot.point.set( Point_add(lOppositeKnot.original,diffOpposite) );
        lOppositeKnot.left.set( Point_add(lOppositeKnot.originalLeft ,diffOpposite) );
        lOppositeKnot.right.set( Point_add(lOppositeKnot.originalRight,diffOpposite) );        
      }
      else
      {
        if (this._oppositeKnotId != undefined)
        {
          this._oppositeKnotId = undefined;
          this._oppositeKnot.point.set(this._oppositeKnot.original);
          this._oppositeKnot.left.set( this._oppositeKnot.originalLeft);
          this._oppositeKnot.right.set( this._oppositeKnot.originalRight);
        }

        forEach( this._points, p => {
          p.point.set( Point_add(p.original,diff) );
          if( p.left ) {
            p.left.set( Point_add(p.originalLeft ,diff) );
          }
          if( p.right ) {
            p.right.set( Point_add(p.originalRight,diff) );
          }
        });
        }
      this.redraw();
      this.callParent(event);
    },

    up( /*view,event*/) {
      this.onUpdate();
      this.undoState();
    }
  }),

  'MovingControl': BaseState.extend({
// =========================================================
    init( tool,side,fromPoint) {
      const _this = this;
      _this.callParent(tool);
      _this._side = side;
      _this._fromPoint = fromPoint;
      const knot = _this._knot = _this.selectedKnot();
      _this._knotPoint = knot.point();
      _this._point = knot.controlPoint(side);
      _this._original = _this._point.clone();
      _this._corner = knot.corner();
      const opp = _this._opp = knot.controlPoint(oppSide(side));
      if( opp ) {
        _this._originalOpp = opp.clone();
      }
      _this._handle    = new Handle.Control(_this,side);
      _this._oppHandle = new Handle.Control(_this,oppSide(side));
    },

    draw( context, transform ) {
      this._handle.draw(context,transform);
      this._oppHandle.draw(context,transform);
      this.callParent(context,transform);
    },

    down() {
      this.state('Iddle');
      return false;
    },

    move( event ) {
      const _this = this,
          fromPoint = _this._fromPoint,
          toPoint   = _this.curvePoint(event),
          diff = Point_substract(toPoint,fromPoint),
          point = _this._point,
          opp = _this._opp,
          knotPoint = _this._knotPoint;

      point.set( Point_add(_this._original,diff) );
      _this._handle._point = point;

      if( opp ) {
        const oppVec = Point_substract(point,knotPoint).sm(-1);
        if( _this._corner === 'curve' ) {
          if( ! oppVec.isZero()) {
            const oppNorm = Point_substract(opp,knotPoint).norm();
            oppVec.sm(oppNorm/oppVec.norm());
          }
          opp.set( Point_add(knotPoint,oppVec) );
        }
        else if( _this._corner === 'symmetric' ) {
          opp.set( Point_add(knotPoint,oppVec) );
        }
        _this._oppHandle._point = opp;
      }

      _this.redraw();
    },

    up() {
      this.onUpdate();
      this.undoState();
    }
  })
// =========================================================
};

let NodeEditBaseTool = Tool.extend({
  Properties: {
    alpha: { type: 'number', def: 1 }
  },
  initTool( view, figure ) {
    if( figure && this.canEdit(figure) ) {
      return this.startEditingFigure$(figure);
    }
  },
  startEdition( /*figure*/) {

  },
  startEditingFigure$( figure ) {
    const _this = this, view = this.view;
    _this._figure = null;
    return figure.update$() .then( () => {
    view.unlock();
    _this._figure = figure;
    _this.startEdition(figure);
    _this._matrix = figure.matrix_();
    _this._matrixInverted = _this._matrix.inverted();
    _this._curves = _this.createCurves(figure);
    _this._undoState = [];
    _this._selectedKnots = [];
    _this._state = new State.Iddle(_this);
    _this.redraw();
    view.preview$();
    });
  },

  on_keydown({ view, event, altKey, which }) {
    if( altKey ) {
      event.preventDefault();
      this.on_keydown_alt(event);
    }

    if( ! view._keyShortcuts ) {
      return;
    }

    switch( which ) {
      case 46: // 'Del'
      event.preventDefault();
      this._state.deleteAction();
      break;
    }
  },

  on_keyup({ event, altKey }) {
    if( ! altKey ) {
      event.preventDefault();
      return this.on_keyup_alt(event);
    }
  },

  on_keydown_alt({ view }) {
    if( ! this._transparentMode ) {
      this._transparentMode = true;
      view.redraw('document');
      this.redraw();
      view.render$();
    }
  },
  on_keyup_alt({ view }) {
    if( this._transparentMode ) {
      this._transparentMode = false;
      view.redraw('document');
      this.redraw();
      view.render$();
    }
  },

  state( s, sk, ...params ) {
    const _this = this, undoState = _this._undoState;
    undoState.push( _this._state );
    if( undoState.length > 10 ) { undoState.shift(); }

    _this._selectedKnots = !sk ? [] : isArray(sk) ? sk : [sk];
    _this._state = new State[s](_this,...params);
    _this.redraw();
  },

  undoState() {
    this._state = this._undoState.pop();
    this._state.focus();
    this.redraw();
  },

  somethingToDraw() {
    return !!this._figure;
  },

  draw({ view, context, transform }) {
    if( !this._figure ) {
      return;
    }

    // Draw figure being edited
    view.drawIsolatedFigures( this._alpha * ( this._transparentMode ? 0.6 : 1 ) );

    // Draw curves, knots and handles
    context.lineJoin = 'round';
    const ft = Matrix.m(transform,this._matrix);
    this._state.draw(context,ft);
  },

  try_pointerdown({ event, isPrimary, docPoint }) {
    if( !this._figure ) {
      return;
    }
    if( ! isPrimary ) {
      return; // ignore multitouch
    }
    if( ! this._state.down(event) ) {
      this._clickOut = { docPoint };
    }
    else {
      event.stopPropagation();
    }
  },

  on_pointerdown(event) {
    this.try_pointerdown(event);
  },

  on_mousemove({ event }) {
    if( !this._figure ) {
      return;
    }
    this._state.mousemove(event);
  },

  on_pointermove({ event, isPrimary }) {
    if( !this._figure ) {
      return;
    }
    if( ! isPrimary ) {
      return; // ignore multitouch
    }
    const co = this._clickOut;
    if( co && ! event.docPoint.isEqual( co.docPoint ) ) {
      this._clickOut = null;
    }
    this._state.move(event);
  },

  on_pointerup({ isPrimary, event }) {
    if( ! isPrimary ) {
      return; // ignore multitouch
    }
    if( this._figure ) {
      this._state.up(event);
    }

    if( this._clickOut || ! this._figure ) {
      this._clickOut = null;
      this._onClickOut(event);
    }
  },

  _onClickOut({ view, event, docPoint, cmdKey }) {

    const _this = this;
    const figure = event.figure; // [EVENTS] view.figureAtPoint( docPoint );
    if( figure && _this.canEdit(figure) ) {
      _this.commitChanges$({ select: false }) .then( () => {
      _this.startEditingFigure$(figure);
      });
    }
    else if( cmdKey ) {
      _this.commit$();
    }
  },

  on_dblclick( event ) {
    if( !this._figure || !this._state.on_dblclick(event) ) {
      this.commit$();
    }
  },

  onUpdate() {
    // ...
  }
});

export const NodeEditTool = NodeEditBaseTool.extend({ typeName: 'NodeEditTool',
  toolbarName: 'nodeEditTool',
  _hideOriginal: true,
  redraw() {
    this.callParent();
    if( this._figure ) {
      this._figure.invalidateView();
    }
    this.view.preview$();
  },
  createCurves( figure ) {
    const curves = [];
    forEach( figure.polyregions, p => {
      forEach( p.regions, r => {
        forEach( r.curves, (c,i) => {
          if( ! ( c instanceof Curve ) ) {
            c = c.convertToCurve();
            r.curves[i] = c;
          }
          // Curves are not currently properly owned
          // I am thinking that we should remove lazyCDL curves loading so we can just include it in our type system
          c._owner = r;
          curves.push(c);
        });
      });
    });
    return curves;
  },
  canEdit( figure ) {
    return this.view.is(figure,'nodeEditable');
  },
  startEdition( figure ) {
    this.view.startIsolatedFiguresEdition([figure]);
  },
  _cancel$( view, doc ) {
    view.stopIsolatedFiguresEdition();
    view.selectedFigures.set(this._figure);
    return doc.revert$();
  },
  _commit$( view ) {
    view.stopIsolatedFiguresEdition();
    return this.commitChanges$();
  },
  commitChanges$( config ) {
    const view = this.view, doc = view.document;
    // This is really important, grab the first envelope point
    // before the figure is invalidated
    const figure = this._figure;
    if( ! figure ) {
      return resolve();
    }
    if( figure._polyregions.length === 0 ) {
      view.remove(figure);
      return doc.commit$();
    }

    const referencePoint = figure.referencePoint_().clone();

    figure.invalidate();
    figure.visible = true;

    if( config && config.select !== false ) {
      view.selectedFigures.set(figure);
    }

    return if$( ! ( figure._anchor._absolute && figure._pin.anchorBased() ), () => {
      return figure._fixUpPin$('referencePoint_',referencePoint);
    }).then( () => {
      if( figure.document !== doc ) {
        view.add( figure );
      }
      return doc.commit$();
    });
  }
});
Tool.NodeEdit = NodeEditTool;

export const NodeEditEnvelopeTool = NodeEditBaseTool.extend({ typeName: 'NodeEditEnvelopeTool',
  toolbarName: 'nodeEditEnvelopeTool',
  canEdit( figure ) {
    return ( figure.isText && ! figure.isMultiPart ) ||
           ( !! findByType( figure._processes, Process.FitEnvelope ) ) ;
  },

  startEdition( figure ) {
    this.view.startIsolatedFiguresEdition([figure]);
  },
  _cancel$( view, doc ) {
    if( Config.localFitEnvelope ) {
      return view.revert$();
    }
    else {
      view.selectedFigures.set(this._figure);
    }
    view.stopIsolatedFiguresEdition();
  },
  _commit$( view ) {
    view.stopIsolatedFiguresEdition();
    return this.commitChanges$();
  },

  createCurves( figure ) {
    // We create a envelope { figure, process, editable } for every envelope in the figure
    let process = findByType(figure.processes,Process.FitEnvelope) ;
    this._addOnCommit = !process;
    if( ! process ) {
      process = Process.FitEnvelope.createFromFigure(figure);
    }
    this._process = process;

    // Convert the envelope to one closed curve
    let pieces = [] ;
    forEach( process.envelope().curves, c => {
      // Flag it
      const ps = cloneDeep(c.pieces);
      ps[0].P0.__cadx_locked = true ;
      pieces = pieces.concat(ps);
    });

    return [ Curve.fromNonTiedPieces(pieces,true) ];
  },
  onUpdate() {
    if( Config.localFitEnvelope ) {
      const view = this.view;
      return this.updateFigure$() .then( () => {
      view.unlock();
      this.redraw();
      view.preview$();
      });
    }
  },
  updateFigure$() {
    const _this = this;
    const figure = _this._figure;
    if( ! figure ) {
      return resolve();
    }
    const process = _this._process;
    const addOnCommit = _this._addOnCommit;
    return if$( addOnCommit, () => {
      _this._addOnCommit = false;
      return figure.center$() .then( center => {
      figure._processes.add(process);
      figure.pin = center;
      });
    }) .then( () => {

    return figure.matrix$() .then( matrix => {

    const oldEnvelopeCenter = matrix.apply( process._envelope.center );

    const curve = _this._curves[0];

    // Get the four curves from envelopeCurve
    const curves = [];
    let currentPieces = [];
    const createCurve = function() {
      if( currentPieces.length > 0 ) {
        curves.push( new Curve.fromNonTiedPieces(currentPieces,false) );
        currentPieces = [] ;
      }
    };
    forEach( curve.pieces, p => {
      if( p.P0.__cadx_locked === true ) {
        createCurve();
      }
      currentPieces.push(p.clone());
    });
    createCurve();

    process.envelope( new Envelope(curves) );

    const newEnvelopeCenter = matrix.apply( process._envelope.center );

    const offset = Point.substract(newEnvelopeCenter,oldEnvelopeCenter);
    figure.translate( offset );

    return figure;
    });});
  },
  commitChanges$( config ) {
    const view = this.view, figure = this._figure;
    return this.updateFigure$() .then( () => {
      if( config && config.select !== false ) {
        view.selectedFigures.set(figure);
      }
      return view.commit$();
    });
  }
});
Tool.NodeEditEnvelope = NodeEditEnvelopeTool;



/** WEBPACK FOOTER **
 ** ./src/ui/tool/node-edit.js
 **/